import { framesPerAttack, framesToHit, resolveAttack } from './combat.js';
import { DEFAULT_POISON_DURATION_TICKS, applyAttack, canAct } from './effects.js';
import { distance } from './geometry.js';
import { hasLineOfSight } from './pathfinding.js';
import { TICK_RATE_HZ } from './tick.js';
import { entityById } from './world.js';
/**
 * Whether one body may hit another right now — and, once it may, one swing from
 * the roll to the wound.
 *
 * ## Why this module exists at all
 *
 * Three modules already own a third of a fight each, and none of them can own
 * this question:
 *
 *   - `combat.ts` rolls a swing between two **stat blocks**. It has no idea
 *     where either of them is standing and must not: it is the layer a client
 *     replays to check a number.
 *   - `effects.ts` lands a result on **one body's life**. It knows nothing about
 *     ids, positions or floors, deliberately (`TODO.md` #221).
 *   - `world.ts` owns **where a body is**, and nothing about what a body can do.
 *
 * So "can this body reach that one, right now" fell between them, and until it
 * was written a player had no way to hit anything: `monster.ts` answered the
 * question privately, for monsters, and the answer was unreachable from
 * anywhere else (`TODO.md` #277). Restating it in a room would have been the
 * worst outcome available — a rule with two homes, one of which the client
 * cannot run.
 *
 * This module is that answer, stated once for **both sides of a fight**.
 * `monster.ts` composes it from above (a brain decides *whether it wants to*
 * swing; this says whether it *could*), and a Colyseus room composes it from the
 * other direction, to validate what a client asked for.
 *
 * ## The anti-cheat shape of it (`ARCHITECTURE.md`)
 *
 * *"Clients send intents only. The server validates every range, cooldown and
 * inventory operation... Nothing client-reported is trusted."* An attack intent
 * carries an id and nothing else — never a damage number, never a position, and
 * never an attacker — and `admitSwing` is what a server turns that id into a
 * swing with. Every refusal it can return is a thing a client might try:
 *
 *   | refusal            | what the client tried                              |
 *   |--------------------|----------------------------------------------------|
 *   | `unembodied`       | swinging with no body on this floor                 |
 *   | `attacker-dead`    | swinging while dead                                 |
 *   | `cooling`          | swinging faster than the breakpoints allow          |
 *   | `no-target`        | naming a body that does not exist, or is elsewhere  |
 *   | `friendly`         | naming itself, or a party member                    |
 *   | `target-dead`      | naming a corpse                                     |
 *   | `out-of-reach`     | naming a body across the floor                      |
 *   | `no-line-of-sight` | naming a body through a wall                         |
 *
 * The order is the design: everything about the **swinger** first, then about
 * the **target**, then about the **space between them**. A dead attacker is
 * refused for being dead whatever it named, and the two lookups that cost
 * anything — the reach measurement and the sight trace — are the last things
 * reached rather than the first.
 *
 * One thing this answers only for the instant it is asked, and **every** caller
 * now has to know it: **reach is a fact about two positions, and positions
 * move.** Every other refusal above survives a caller queueing the decision — an
 * id, a kind and a life do not change under a body walking — but a swing is no
 * longer resolved where it is admitted. It is admitted here, and it lands
 * `attackWindUpTicks` later, at the frame D2's ActionFlag puts the blow on, with
 * a whole movement step per tick in between. The reach validated at the request
 * is not the reach the blow is thrown across. `canReach` is here so that a
 * caller can ask again where the swing actually lands, against the same rule and
 * with the same default (`apps/game`'s `world/floor-tick.ts`, `TODO.md` #293,
 * #483). Asking again costs nothing and draws nothing, which is what makes it
 * safe to do inside a tick.
 *
 * ## The wind-up, and what it means for a caller
 *
 * `attackCooldownTicks` says how long a swing lasts; `attackWindUpTicks` says
 * how far into it the blow lands, and `windingUp` is the same fact as a yes or
 * no for anything that has to draw the anticipation. A swing is therefore three
 * moments and not one — admitted, landed, finished — and the middle one is where
 * everything is judged:
 *
 *   - **Every check is re-made at the landing tick**, not at the request. Both
 *     lives, the reach, the target still standing on this floor. A target that
 *     dies, walks out of reach or leaves during the wind-up is a swing that
 *     whiffs, which is D2's own answer and the thing that makes a telegraph
 *     worth reacting to.
 *   - **Nothing may be drawn from the `Rng` until the blow lands.** A roll taken
 *     at request time cannot be reproduced by a replay of the landing tick: the
 *     stream position it was drawn at belongs to a different tick on one side of
 *     the wire and not the other.
 *
 * ## Determinism
 *
 * Nothing here draws from the `Rng` except `resolvePlayerAttack`, which draws
 * exactly what `resolveAttack` draws, in the same order — the fixed roll budget
 * (`combat.ts`) is a wire contract, and a swing that spent an extra number would
 * reseat every roll after it on one side of the wire and not the other. A
 * refused swing draws **nothing at all**, which is why a caller must refuse
 * before it rolls rather than roll and discard — and why moving *when* a swing
 * is rolled changes nothing about *how many* values it costs.
 */
/**
 * D2's animation clock, in frames per second.
 *
 * The number every frame-count in `combat.ts` is denominated in: attack speed is
 * a staircase of *frames*, and a frame is a twenty-fifth of a second. The
 * simulation runs at 20 Hz (`tick.ts`), so the two grids do not line up and
 * something has to convert — `attackCooldownTicks` is that something, and this
 * is the only place the 25 appears.
 */
export const ANIMATION_FRAMES_PER_SECOND = 25;
/**
 * The clear air between two bodies: the distance between their centres, less
 * both radii.
 *
 * Every range in a fight is measured with it — aggro, reach, and the pick of the
 * nearest player — because a body is a disc and the question being asked is
 * always about the space *between* two of them. It goes **negative** when two
 * bodies overlap, which is an ordinary state rather than an error: a monster
 * walks onto a stationary player (`TODO.md` #143) and separation may shove one
 * there.
 */
export const bodyGap = (a, b) => distance(a.position, b.position) - a.mover.radius - b.mover.radius;
/**
 * How many simulation ticks one swing of this profile costs.
 *
 * `combat.ts` answers attack speed in D2's own currency — whole animation frames
 * at 25 fps — and the simulation steps at 20 Hz, so the conversion is
 * `frames × 20 / 25`, and the tick grid is the coarser of the two.
 *
 * Rounded **up**, always. Rounding down would hand every swinger a breakpoint
 * nobody paid for and would do it silently; up means a breakpoint may buy
 * nothing at all — which is exactly D2's own staircase, one grid coarser — but
 * can never buy something extra. It also cannot round to zero: the fastest swing
 * `combat.ts` will produce is one frame, which is 0.8 of a tick and therefore
 * one, and a swing that resolved between two steps would be a swing the
 * simulation never took.
 */
export const attackCooldownTicks = (attack) => Math.ceil((framesPerAttack(attack) * TICK_RATE_HZ) / ANIMATION_FRAMES_PER_SECOND);
/**
 * How many simulation ticks pass between a swing starting and its blow landing:
 * the **wind-up**.
 *
 * `attackCooldownTicks` is the length of the animation; this is the moment
 * inside it, converted on the identical grid — `framesToHit` through the same
 * `frames × 20 / 25` as `framesPerAttack`, so the two cannot answer in different
 * currencies.
 *
 * Rounded **up**, for the mirror of the reason the cadence is. Rounding down
 * would land a blow *before* the frame the animation lands it on — tempo the
 * profile did not buy — and, worse, would round a one-frame anticipation to
 * zero, which is a telegraph that does not exist. Up means the blow is never
 * early, and the invariant that matters is preserved either way:
 * **`attackWindUpTicks(a) <= attackCooldownTicks(a)` for every profile**, because
 * `framesToHit` is clamped inside `framesPerAttack` and this grid is monotone.
 *
 * Zero is a legal answer and means what it says: an animation whose ActionFlag
 * sits on frame 0 lands its blow the instant the swing starts. That is the
 * behaviour this game had before a hit frame existed (`TODO.md` #483) and it
 * stays expressible on purpose — a wind-up nobody can turn off is a constant
 * rather than a rule.
 */
export const attackWindUpTicks = (attack) => Math.ceil((framesToHit(attack) * TICK_RATE_HZ) / ANIMATION_FRAMES_PER_SECOND);
/**
 * The tick a swing started on `swingTick` lands its blow on.
 *
 * One line, and exported rather than inlined because it is the **one** place the
 * arithmetic happens. A caller that schedules the blow and a caller that draws
 * the anticipation are asking the same question, and the moment they add the
 * wind-up up separately the art and the damage can disagree — which is the whole
 * failure `TODO.md` #483 exists to prevent. `windingUp` below is defined in
 * terms of this for exactly that reason.
 *
 * The wind-up is passed rather than the profile because a monster carries its
 * cadence as ticks on its brain (`monster.ts`) and a character carries a
 * profile: both hand this the same number, from `attackWindUpTicks`, and there
 * is one definition of *when*.
 */
export const hitTick = (windUp, swingTick) => swingTick + windUp;
/**
 * Whether a swing taken on `lastSwingTick` has not yet reached its hit frame.
 *
 * The anticipation window, as a yes or no: true from the tick the swing started
 * up to but **not including** the tick the blow lands on. That boundary is the
 * honesty property in one line — the telegraph ends exactly where the damage
 * begins, so a client drawing a wind-up is drawing time a player genuinely has,
 * and stops drawing it on the frame the blow arrives.
 *
 * It is what `apps/game` derives its `winding-up` action from, so that the
 * length of a telegraph is never a number that app wrote down.
 *
 * `null` — nothing swung yet — is not winding up, and neither is a tick behind
 * the swing: a clock that went backwards grants no credit, exactly as
 * `readyToSwing` refuses to.
 */
export const windingUp = (windUp, tick, lastSwingTick) => lastSwingTick !== null && tick >= lastSwingTick && tick < hitTick(windUp, lastSwingTick);
/**
 * How far a character can land a blow, in cells of **clear air between the two
 * bodies** — not between their centres.
 *
 * Half a cell: arm's length, the same air a melee monster stands at
 * (`ARCHETYPE_REACH`), and stated here rather than borrowed from that table
 * because a character's weapon is not a monster's archetype — the day one of the
 * two is tuned, the other must not move with it.
 *
 * **A placeholder**, in exactly the sense `DEFAULT_MOVE_SPEED` and
 * `DEFAULT_POISON_DURATION_TICKS` are: reach is a property of the *weapon* (D2
 * gives a spear more of it than a dagger), weapons carry no range yet
 * (`TODO.md` #22, #30), and every call site inventing its own number is how
 * client and server end up disagreeing about what a swing can touch. So core
 * states one, here, beside the rule that consumes it.
 */
export const DEFAULT_ATTACK_REACH = 0.5;
/**
 * The reach rule: two conditions, and the second is what keeps range from
 * meaning a shot.
 *
 * A body that could not see its target and swung anyway would be swinging
 * through rock — and on a cave floor, "through rock" is a corner a client can
 * hide behind while it kills what is on the other side of it. Reach is a
 * **ceiling and not a band**: a body standing *inside* another can hit it, which
 * is the state separation and a monster's own chase can both produce.
 *
 * Sight is traced as a **point**, without the swinger's own body. A blow is not
 * a body: a fat monster is not blinded by a corridor it merely cannot walk down,
 * and anything in melee reach of something is by definition next to it.
 *
 * Exported rather than private because a swing is not the only thing that has to
 * cross the gap: `casting.ts` measures a bolt, a curse and a thrown javelin with
 * this, at the range the skill carries. "Can this body touch that one" is one
 * rule with one home whatever is crossing, and `canReach` is the same question
 * asked as a yes or no.
 */
export const reachFailure = (world, attacker, target, reach = DEFAULT_ATTACK_REACH) => {
    if (bodyGap(attacker, target) > reach)
        return 'out-of-reach';
    if (!hasLineOfSight(world.grid, attacker.position, target.position))
        return 'no-line-of-sight';
    return null;
};
/**
 * Whether `attacker` could land a blow on `target` from where it stands.
 *
 * The whole of the reach rule as a yes or no, for the callers that only want
 * that: a monster brain deciding whether to close or swing (`monster.ts`), and a
 * client greying out an attack it knows the server would refuse.
 */
export const canReach = (world, attacker, target, reach) => reachFailure(world, attacker, target, reach) === null;
/**
 * Whether these two are on opposite sides of a fight.
 *
 * Stated as a rule about a **pair**, like `world.ts`'s `obstructs` and
 * `monster.ts`'s `isPrey`, because that is the shape of the decision: monsters
 * hunt players, players hunt monsters, and nothing swings at its own kind. Two
 * things fall out of it that a room would otherwise have to state for itself —
 * there is no friendly fire in this co-op game, and a body cannot swing at
 * itself, both of which are things a client will try.
 *
 * A third kind — a summon, a mercenary, a destructible prop — re-opens it, and
 * `EntityKind` is where that question already lives (`TODO.md` #102).
 */
export const isHostile = (attacker, target) => attacker.kind !== target.kind;
/**
 * Whether a body that last swung on `lastSwingTick` may swing again on `tick`.
 *
 * The cooldown half of the anti-cheat rule, and the reason it is core's: attack
 * speed is a **staircase of breakpoints** (`combat.ts`), so how often a swing may
 * be taken is a game rule with real gear behind it, not a throttle a server
 * invents. A client that sends twenty attacks in one tick gets one swing and
 * nineteen refusals, at whatever cadence its profile actually bought.
 *
 * `null` — nothing swung yet — is ready. A `tick` at or behind the last swing is
 * not: a clock that went backwards grants no credit, exactly as the rate limiter
 * refuses to, because the alternative is a way to buy a free swing.
 */
export const readyToSwing = (attack, tick, lastSwingTick) => lastSwingTick === null || tick - lastSwingTick >= attackCooldownTicks(attack);
/**
 * Decides whether one body may swing at another right now.
 *
 * Pure, and it draws nothing: a refused swing costs no roll, so a client
 * hammering the server cannot advance the random stream by asking. See the
 * module header for the order of the checks and for what each refusal is
 * protecting against.
 *
 * It deliberately answers only "may this swing happen". *Whether it hits* is
 * `resolveAttack`'s, and a swing that is admitted and then misses is an ordinary
 * outcome — the same way a click on rock is an ordinary click that walks as
 * close as it can rather than a refusal.
 */
export const admitSwing = (request) => {
    const attacker = entityById(request.world, request.attackerId);
    if (attacker === null)
        return { ok: false, reason: 'unembodied' };
    // A corpse does not swing, and neither does a body this floor tracks no life
    // for: `canAct` is where "absence fails closed" is stated, once, for every
    // gate that has to enforce it (`effects.ts`).
    if (!canAct(request.attackerVitals))
        return { ok: false, reason: 'attacker-dead' };
    if (!readyToSwing(request.attack, request.tick, request.lastSwingTick))
        return { ok: false, reason: 'cooling' };
    const target = entityById(request.world, request.targetId);
    if (target === null)
        return { ok: false, reason: 'no-target' };
    if (!isHostile(attacker, target))
        return { ok: false, reason: 'friendly' };
    if (!canAct(request.targetVitals))
        return { ok: false, reason: 'target-dead' };
    const failure = reachFailure(request.world, attacker, target, request.reach);
    if (failure !== null)
        return { ok: false, reason: failure };
    return { ok: true, attacker, target };
};
/**
 * A character's swing, rolled and landed on the body in one call.
 *
 * Nothing but composition — `resolveAttack` rolls it and `applyAttack` settles
 * it — with one rule carried across the seam: the source is `'character'`, so a
 * second poison **replaces** the rate already running rather than adding to it
 * (`effects.ts`, `TODO.md` #230). That happens to be `applyAttack`'s default,
 * which is exactly why it is stated: the monster wrapper next door must pass
 * `'monster'` and this one must not, and a call site that composed the two by
 * hand would eventually get the pair the wrong way round. `EffectSource`'s
 * `'character'` covers a mercenary and a summon too, which is where their swings
 * will come through when either exists.
 *
 * The roll budget is `resolveAttack`'s, untouched: this draws exactly what a
 * swing draws, in the same order, so a replay reproduces it.
 */
export const resolvePlayerAttack = (attacker, defender, vitals, rng, poisonDurationTicks = DEFAULT_POISON_DURATION_TICKS) => {
    const result = resolveAttack(attacker, defender, rng);
    return { result, change: applyAttack(vitals, result, poisonDurationTicks, 'character') };
};
//# sourceMappingURL=attack.js.map